
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/lib/supabase';
import { Bot } from '@/types/trader';

export const createShadowBot = async (originalBot: Bot): Promise<void> => {
  console.log('Creating shadow bot for:', originalBot.name);
  
  // Fetch the latest bot data from database to ensure we have correct token values
  const { data: latestBot, error: fetchError } = await supabase
    .from('bots')
    .select('*')
    .eq('id', originalBot.id)
    .single();
    
  if (fetchError) {
    console.error('Error fetching latest bot data:', fetchError);
    throw fetchError;
  }
  
  if (!latestBot) {
    throw new Error('Bot not found in database');
  }
  
  console.log('Latest bot tokens - signal:', latestBot.signal_token, 'reverse:', latestBot.reverse_signal_token);
  
  const shadowBot = {
    id: uuidv4(),
    name: `${latestBot.name} (Shadow)`,
    description: `Shadow bot for ${latestBot.name}`,
    status: latestBot.status,
    type: 'shadow_bot' as const,
    created_date: new Date().toISOString(),
    last_updated: new Date().toISOString(),
    risk: latestBot.risk,
    image_url: latestBot.image_url,
    exchange: latestBot.exchange,
    owner_id: latestBot.owner_id,
    external_id: latestBot.external_id,
    is_featured: false,
    is_new: false,
    is_best_seller: false,
    signal_token: latestBot.reverse_signal_token, // Shadow bot's signal token is original's reverse token
    reverse_signal_token: latestBot.signal_token, // Shadow bot's reverse token is original's signal token
    webhook_url: 'https://api.tradebot365.com/api/webhook/order',
    is_deleted: false,
    bot_id: uuidv4(),
    signal_from: latestBot.signal_from,
    color_scheme: latestBot.color_scheme,
  };

  console.log('Creating shadow bot with tokens - signal:', shadowBot.signal_token, 'reverse:', shadowBot.reverse_signal_token);

  const { error } = await supabase
    .from('bots')
    .insert([shadowBot]);
    
  if (error) {
    console.error('Error creating shadow bot:', error);
    throw error;
  }
  
  console.log('Shadow bot created successfully with ID:', shadowBot.id);
};

export const deleteShadowBot = async (originalBotId: string): Promise<void> => {
  console.log('Deleting shadow bot for original bot:', originalBotId);
  
  // Find the original bot to get its reverse_signal_token
  const { data: originalBot, error: fetchError } = await supabase
    .from('bots')
    .select('reverse_signal_token')
    .eq('id', originalBotId)
    .single();
    
  if (fetchError) {
    console.error('Error fetching original bot:', fetchError);
    return; // Don't throw here as the main bot deletion should still proceed
  }
  
  if (!originalBot) {
    console.log('Original bot not found, skipping shadow bot deletion');
    return;
  }
  
  // Delete the shadow bot using the reverse_signal_token as signal_token
  const { error: deleteError } = await supabase
    .from('bots')
    .delete()
    .eq('signal_token', originalBot.reverse_signal_token)
    .eq('type', 'shadow_bot');
    
  if (deleteError) {
    console.error('Error deleting shadow bot:', deleteError);
    // Don't throw here as the main bot deletion should still proceed
  } else {
    console.log('Shadow bot deleted successfully');
  }
};
